import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Ban, Check, FileText, ReceiptText } from "lucide-react";
import { StatusBadge, PaymentBadge } from "./AdminBookings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBilling = {
  name: "",
  country: "Magyarország",
  postal_code: "",
  city: "",
  address: "",
  tax_number: "",
};

const requiredBillingKeys = ["name", "country", "postal_code", "city", "address"];
const billingComplete = (billing = {}) =>
  requiredBillingKeys.every((key) => String(billing[key] || "").trim());

const invoiceStatusLabel = (status) => {
  const labels = {
    pending_payment: "Fizetésre vár",
    ready: "Kiállítható",
    issuing: "Kiállítás folyamatban",
    issued: "Kiállítva",
    error: "Hiba",
  };
  return labels[status] || status || "—";
};

export default function AdminBookingDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [form, setForm] = useState({});

  const hydrate = useCallback((data) => {
    setB(data);
    setForm({
      status: data.status,
      payment_status: data.payment_status,
      payment_provider: data.payment_provider || "",
      payment_reference: data.payment_reference || "",
      billing: { ...emptyBilling, ...(data.billing || {}) },
      admin_notes: data.admin_notes || "",
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings/${id}`, { withCredentials: true });
      hydrate(r.data);
    } catch (e) {
      toast.error("Nem található a foglalás");
    } finally {
      setLoading(false);
    }
  }, [id, hydrate]);

  useEffect(() => { load(); }, [load]);

  const buildPayload = (override = {}) => {
    const payload = { ...form, ...override };
    const billing = payload.billing || {};
    // Older bookings predate invoice fields. Keep those records editable: only
    // send the strict billing object once all legally required address fields
    // have been completed by the admin.
    if (!billingComplete(billing)) {
      delete payload.billing;
    }
    return payload;
  };

  const save = async (override = {}) => {
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API}/admin/bookings/${id}`,
        buildPayload(override),
        { withCredentials: true }
      );
      hydrate(r.data);
      toast.success("Mentve");
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const setBilling = (key, value) => {
    setForm((current) => ({
      ...current,
      billing: { ...emptyBilling, ...(current.billing || {}), [key]: value },
    }));
  };

  const issueInvoice = async () => {
    if (!billingComplete(form.billing)) {
      toast.error("A számla kiállításához töltsd ki a teljes számlázási címet.");
      return;
    }
    setInvoicing(true);
    try {
      // Save corrected billing data before creating a legal document.
      const saved = await save();
      if (!saved) return;
      const r = await axios.post(
        `${API}/admin/bookings/${id}/invoice`,
        {},
        { withCredentials: true }
      );
      hydrate(r.data);
      toast.success(`Számla kiállítva: ${r.data.invoice_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A számla kiállítása sikertelen");
      await load();
    } finally {
      setInvoicing(false);
    }
  };

  if (loading) return <p className="text-[var(--ink-soft)] italic">Betöltés…</p>;
  if (!b) return <p className="text-[var(--ink-soft)]">Nem található.</p>;

  const pricing = b.pricing || {};
  const billing = form.billing || emptyBilling;
  const canIssueInvoice =
    b.payment_status === "paid" &&
    b.szamlazz_configured &&
    !b.invoice_number &&
    b.invoice_status !== "issuing" &&
    billingComplete(billing);

  return (
    <div data-testid={`admin-booking-detail-${id}`}>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs tk-link-soft mb-4"><ArrowLeft size={13} />Vissza a listához</Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[var(--ink)]">{b.customer_name}</h1>
          <p className="text-sm text-[var(--ink-soft)]">{b.customer_email} · {b.customer_phone || "—"}</p>
        </div>
        <div className="flex gap-2">
          <StatusBadge status={b.status} /><PaymentBadge payment_status={b.payment_status} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="tk-card p-6 space-y-3">
            <h3 className="font-serif text-xl mb-2">Esemény részletei</h3>
            <Row k="Esemény dátuma" v={b.event_date} />
            <Row k="Helyszín" v={b.event_location} />
            <Row k="Esemény típusa" v={b.event_type} />
            <Row k="Kiválasztott telefon" v={b.device ? <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full border" style={{ background: b.device.color }} />{b.device.name}</span> : "—"} />
            <Row k="Csomag" v={b.selected_package} />
            <Row k="Ügyfél üzenete" v={<span className="whitespace-pre-wrap">{b.message || "—"}</span>} />
            <Row k="Létrehozva" v={new Date(b.created_at).toLocaleString("hu-HU")} />
            <Row k="Frissítve" v={b.updated_at ? new Date(b.updated_at).toLocaleString("hu-HU") : "—"} />
          </div>

          <div className="tk-card p-6">
            <div className="flex items-start gap-3 mb-5">
              <span className="w-10 h-10 rounded-full bg-[rgba(110,30,43,0.08)] text-[var(--burgundy)] flex items-center justify-center shrink-0">
                <ReceiptText size={18} />
              </span>
              <div>
                <h3 className="font-serif text-xl">Számlázási adatok</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-1">Ezek az adatok kerülnek a Számlázz.hu bizonylatra.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <AdminField label="Név / cégnév" value={billing.name} onChange={(v) => setBilling("name", v)} />
              <AdminField label="Ország" value={billing.country} onChange={(v) => setBilling("country", v)} />
              <AdminField label="Irányítószám" value={billing.postal_code} onChange={(v) => setBilling("postal_code", v)} />
              <AdminField label="Település" value={billing.city} onChange={(v) => setBilling("city", v)} />
              <div className="sm:col-span-2">
                <AdminField label="Cím" value={billing.address} onChange={(v) => setBilling("address", v)} />
              </div>
              <AdminField label="Adószám (opcionális)" value={billing.tax_number || ""} onChange={(v) => setBilling("tax_number", v)} />
            </div>
          </div>

          <div className="tk-card p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-[rgba(110,30,43,0.08)] text-[var(--burgundy)] flex items-center justify-center shrink-0">
                  <FileText size={18} />
                </span>
                <div>
                  <h3 className="font-serif text-xl">Számlázás</h3>
                  <p className="text-sm text-[var(--ink-soft)] mt-1">
                    Számlázz.hu: {b.szamlazz_configured ? "beállítva" : "nincs Agent-kulcs"}
                    {b.szamlazz_configured ? ` · automatikus: ${b.szamlazz_auto_invoice ? "bekapcsolva" : "kikapcsolva"}` : ""}
                  </p>
                </div>
              </div>
              <span className="rounded-full px-3 py-1.5 text-xs bg-[rgba(110,30,43,0.07)] text-[var(--burgundy)]">
                {invoiceStatusLabel(b.invoice_status)}
              </span>
            </div>

            <div className="mt-5 space-y-2">
              <Row k="Számlaszám" v={b.invoice_number} />
              <Row k="Végösszeg" v={Number.isFinite(Number(pricing.total_gross_huf)) ? `${Number(pricing.total_gross_huf).toLocaleString("hu-HU")} Ft` : "—"} />
              <Row k="Árlista verzió" v={pricing.version} />
              <Row k="Számla kiállítva" v={b.invoice_issued_at ? new Date(b.invoice_issued_at).toLocaleString("hu-HU") : "—"} />
            </div>

            {b.invoice_error && (
              <div className="mt-4 rounded-2xl border border-[rgba(110,30,43,0.20)] bg-[rgba(110,30,43,0.04)] p-4 text-sm text-[var(--burgundy)]">
                {b.invoice_error}
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              {!b.invoice_number && (
                <button
                  type="button"
                  onClick={issueInvoice}
                  disabled={!canIssueInvoice || saving || invoicing}
                  className="tk-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ReceiptText size={14} />
                  {invoicing ? "Számla kiállítása…" : b.invoice_status === "error" ? "Számla újrapróbálása" : "Számla kiállítása"}
                </button>
              )}
              {b.invoice_number && (
                <a
                  href={`${API}/admin/bookings/${id}/invoice.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tk-btn-secondary"
                >
                  <FileText size={14} />PDF megnyitása
                </a>
              )}
            </div>
            {b.payment_status !== "paid" && (
              <p className="mt-3 text-xs text-[var(--ink-soft)]">Számla a foglalás kifizetett állapotba állítása után készíthető.</p>
            )}
            {!billingComplete(billing) && (
              <p className="mt-3 text-xs text-[var(--ink-soft)]">A számla kiállításához töltsd ki a számlázási név, ország, irányítószám, település és cím mezőket.</p>
            )}
          </div>
        </div>

        <div className="tk-card p-6 space-y-4 h-fit">
          <h3 className="font-serif text-xl">Admin műveletek</h3>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Foglalás státusza</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-status" className="tk-input">
              {["pending","awaiting_payment","paid","confirmed","cancelled","expired"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Fizetés státusza</label>
            <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="edit-payment-status" className="tk-input">
              {["unpaid","paid","refunded"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Fizetési szolgáltató</label>
            <input value={form.payment_provider} onChange={(e) => setForm({ ...form, payment_provider: e.target.value })} placeholder="Stripe / QuickPay" className="tk-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Fizetési azonosító</label>
            <input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} placeholder="pi_xxx vagy ref" className="tk-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">Belső megjegyzések</label>
            <textarea rows={4} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="edit-admin-notes" className="tk-input resize-none" />
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <button onClick={() => save()} disabled={saving || invoicing} data-testid="save-booking" className="tk-btn-primary justify-center"><Save size={14} />{saving ? "Mentés…" : "Mentés"}</button>
            <button onClick={() => save({ status: "confirmed" })} disabled={saving || invoicing} data-testid="mark-confirmed" className="tk-btn-secondary justify-center"><Check size={14} />Megerősítés</button>
            <button onClick={() => save({ payment_status: "paid" })} disabled={saving || invoicing} data-testid="mark-paid" className="tk-btn-secondary justify-center"><Check size={14} />Kifizetve</button>
            <button onClick={() => save({ status: "cancelled" })} disabled={saving || invoicing} data-testid="cancel-booking" className="tk-btn-secondary justify-center text-[var(--burgundy)]"><Ban size={14} />Lemondás</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const AdminField = ({ label, value, onChange }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">{label}</span>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="tk-input" />
  </label>
);

const Row = ({ k, v }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b last:border-b-0 border-[rgba(110,30,43,0.06)]">
    <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] sm:w-44 shrink-0">{k}</div>
    <div className="text-[var(--ink)] break-words min-w-0">{v || "—"}</div>
  </div>
);
